import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatConnectorType, formatFileType } from '@Helpers';

import { icon_search } from '@Assets';
import { IntegrationList, PageWrapper, Select } from '@Components';
import { useDebounce } from '@CustomHooks';
import { IntegrationFilterOptions, IntegrationType, Options } from '@Models';

const PAGE_SIZE = 9;

interface IntegrationProps {
  integrations: IntegrationType[];
  total: number;
  initialFilterOptions: IntegrationFilterOptions;
}

const Integrations = ({ integrations, total, initialFilterOptions }: IntegrationProps) => {
  const { t } = useTranslation();

  const [selectedClients, setSelectedClients] = useState<Options[]>([]);
  const [selectedFileTypes, setSelectedFileTypes] = useState<Options[]>([]);
  const [selectedConnectorTypes, setSelectedConnectorTypes] = useState<Options[]>([]);
  const [search, setSearch] = useState('');

  const [totalIntegrations, setTotal] = useState(total);

  const [filterOptions, setFilterOptions] = useState<IntegrationFilterOptions>(initialFilterOptions);

  const debouncedSearch = useDebounce(search, 500);

  // Convert strings to Options using helpers
  const fileTypeOptions = useMemo(
    () =>
      filterOptions.file_types
        .map((ft) => ({ value: ft, label: formatFileType(ft) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [filterOptions.file_types],
  );

  const connectorOptions = useMemo(
    () =>
      filterOptions.connector_types
        .map((ct) => ({ value: ct, label: formatConnectorType(ct) }))
        .sort((a, b) => a.label.localeCompare(b.label)),
    [filterOptions.connector_types],
  );

  // Client name mapping
  const clientsObjects = useMemo(() => {
    const object: { [key: string]: string } = {};
    filterOptions.clients.forEach((el) => {
      object[el.value] = el.label;
    });
    return object;
  }, [filterOptions.clients]);

  // Callback to update filter options
  const handleFilterOptionsUpdate = (newOptions: IntegrationFilterOptions) => {
    setFilterOptions(newOptions);
  };

  return (
    <div className="h-full w-full">
      <PageWrapper title={t('Integration.Title')}>
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          <div className="flex flex-col gap-4 mx-6 my-4">
            <span className="font-bold text-xl text-gray-700"> {`${t('Integration.All')} (${totalIntegrations})`}</span>

            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 text-[15px] overflow-hidden">
              <Select
                placeholder={t('Integration.SelectClient')}
                isMulti
                compactMulti
                compactLabel={t('Integration.Client')}
                value={selectedClients}
                options={filterOptions.clients}
                onChange={(selected) => setSelectedClients(selected || [])}
                isClearable
              />

              <Select
                placeholder={t('Integration.SelectFileType')}
                isMulti
                compactMulti
                compactLabel={t('Integration.FileType')}
                value={selectedFileTypes}
                options={fileTypeOptions}
                onChange={(selected) => setSelectedFileTypes(selected || [])}
                isClearable
              />

              <Select
                placeholder={t('Integration.SelectConnector')}
                isMulti
                compactMulti
                compactLabel={t('Integration.Connector')}
                value={selectedConnectorTypes}
                options={connectorOptions}
                onChange={(selected) => setSelectedConnectorTypes(selected || [])}
                isClearable
              />

              <div className="flex items-center gap-2 px-3 rounded border border-solid border-[#E4E7EC] bg-white h-[36px] focus-within:border-purple-500 cursor-text">
                <img src={icon_search} alt="search" className="w-4 h-4 flex-shrink-0 text-gray-600" />
                <input
                  type="text"
                  id="path-search"
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full text-[15px] outline-none border-none bg-transparent placeholder:text-gray-500"
                  placeholder={t('Integration.SearchByPath')}
                />
              </div>
            </div>
          </div>

          <IntegrationList
            integrationsData={integrations}
            total={totalIntegrations}
            setTotal={setTotal}
            pageSize={PAGE_SIZE}
            clientsObjects={clientsObjects}
            selectedClients={selectedClients}
            selectedFileTypes={selectedFileTypes}
            selectedConnectorTypes={selectedConnectorTypes}
            search={debouncedSearch}
            onFilterOptionsUpdate={handleFilterOptionsUpdate}
          />
        </div>
      </PageWrapper>
    </div>
  );
};
export default Integrations;
